import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {MapRangeSockets} from "#material/nodes/converter/map-range/definition"
import {mapRangeDefinition} from "#material/nodes/converter/map-range/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import type {ThreeReturns} from "#material/sockets/three"
import type {MapRangeNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class MapRange extends MaterialNode<MapRangeSockets> {
    public override get definition() {
        return mapRangeDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<MapRangeSockets>> => {
        const {value, fromMin, fromMax, toMin, toMax} = await evaluator.getAll({
            value: this.parameters.inputs.value,
            fromMin: this.parameters.inputs.fromMin,
            fromMax: this.parameters.inputs.fromMax,
            toMin: this.parameters.inputs.toMin,
            toMax: this.parameters.inputs.toMax,
        })
        const fromMinParam = fromMin?.toFloat() ?? threeConvert(this.parameters.settings.fromMin, threeValue) ?? 0
        const fromMaxParam = fromMax?.toFloat() ?? threeConvert(this.parameters.settings.fromMax, threeValue) ?? 1
        const toMinParam = toMin?.toFloat() ?? threeConvert(this.parameters.settings.toMin, threeValue) ?? 0
        const toMaxParam = toMax?.toFloat() ?? threeConvert(this.parameters.settings.toMax, threeValue) ?? 1
        const valueParam = value?.toFloat() ?? threeConvert(this.parameters.settings.value, threeValue) ?? 0
        const clampedValue = this.parameters.settings.clamp ? TSL.max(TSL.min(valueParam, fromMaxParam), fromMinParam) : valueParam
        const normalizedValue = TSL.div(TSL.sub(clampedValue, fromMinParam), TSL.sub(fromMaxParam, fromMinParam))
        // TODO: support interpolation types other than linear, take steps into account
        return {result: TSL.add(TSL.mul(TSL.sub(toMaxParam, toMinParam), normalizedValue), toMinParam)}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<MapRangeSockets>> => {
        const mapRangeNode: MapRangeNode = {
            type: "map_range",
            inputs: await evaluator.getAll({
                from_max: this.parameters.inputs.fromMax,
                from_min: this.parameters.inputs.fromMin,
                to_max: this.parameters.inputs.toMax,
                to_min: this.parameters.inputs.toMin,
                steps: this.parameters.inputs.steps,
                value: this.parameters.inputs.value,
            }),
            parameters: {
                clamp: this.parameters.settings.clamp,
                from_max: this.parameters.settings.fromMax,
                from_min: this.parameters.settings.fromMin,
                to_max: this.parameters.settings.toMax,
                to_min: this.parameters.settings.toMin,
                steps: this.parameters.settings.steps,
                value: this.parameters.settings.value,
                range_type: undefined, //cyclesRangeType(this.parameters.settings.interpolationType as InterpolationType),
            },
            resources: {},
        }

        return {
            result: [mapRangeNode, "result"],
        }
    }
}
